/**
 * Client mirror of `config/exploreUnknownMinigames.json`.
 * Parity: `__tests__/regression/ExploreUnknown_Minigames_Config_Parity_Regression.js`
 */

export const EXPLORE_UNKNOWN_MINIGAME_DEFAULT_SPAWN_CHANCE_PERCENT = 50;
export const EXPLORE_UNKNOWN_MINIGAME_SPAWN_CHANCE_STEP_PERCENT = 25;
export const EXPLORE_UNKNOWN_MINIGAME_SPAWN_CHANCE_FLOOR_PERCENT = 25;
export const EXPLORE_UNKNOWN_MINIGAME_SPAWN_CHANCE_CEILING_PERCENT = 100;

/** @type {Record<string, { minigameId: string | null, enabled: boolean }>} */
export const EXPLORE_UNKNOWN_MINIGAME_SURFACES = {
  arrival: { minigameId: 'beyondBirdMemory', enabled: true },
  cave: { minigameId: 'wombafleksCatch', enabled: true },
  desert: { minigameId: 'rewardMemory', enabled: true },
  fair: { minigameId: 'balloonBoothPop', enabled: true },
  lavender: { minigameId: 'theGamble', enabled: true },
  treetop: { minigameId: 'spottingObjects', enabled: true },
  boat: { minigameId: null, enabled: false },
  pixelpast: { minigameId: null, enabled: false },
  kendrex: { minigameId: null, enabled: false },
};

export const EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_TREE_COUNT = 5;
export const EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_POP_COUNT = 30;
/** Billy Bucks granted when `billyBucks` win reward is rolled (must match server config). */
export const EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_BILLY_BUCKS_AMOUNT = 250;
export const EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_BILLY_BUCKS_AMOUNT = 250;
export const EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_POSITION_INTERVAL_MS = 1000;
export const EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_TIMER_DURATION_MS = 60_000;
export const EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_WIN_PICK_VALUE = 1;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_PAIR_COUNT = 8;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_CARD_COUNT = 16;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_MAX_ATTEMPTS = 4;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_MAX_REWARDS_PER_GAME = 4;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_BILLY_BUCKS_AMOUNT = 250;
export const EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_WIN_PICK_VALUE = 1;
export const EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_BALLOON_COUNT = 20;
export const EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_MAX_POPS = 3;
export const EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_COMPLETION_PICK_VALUE = 3;
export const EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_BILLY_BUCKS_AMOUNT = 250;
export const EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_TARGET_SCORE = 15;
export const EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_WIN_PICK_VALUE = 1;
export const EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_LOSS_PICK_VALUE = 0;
export const EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_LOSS_DARKNESS_INCREMENT_PERCENT = 10;
export const EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_BILLY_BUCKS_AMOUNT = 250;
/** Base (5) + combined (10) factions in the Spotting Objects picklist. */
export const EXPLORE_UNKNOWN_MINIGAME_SPOTTING_OBJECTS_FACTION_COUNT = 15;
export const EXPLORE_UNKNOWN_MINIGAME_SPOTTING_OBJECTS_ORDER_DISPLAY_SCALE = 0.01;
export const EXPLORE_UNKNOWN_MINIGAME_SPOTTING_OBJECTS_BILLY_BUCKS_AMOUNT = 250;

/** Global mission index ranges per surface (aligned with server `exploreUnknownMissions.js`). */
export const EXPLORE_UNKNOWN_MINIGAME_SURFACE_MISSION_RANGES = Object.freeze({
  arrival: { firstMission: 1, lastMission: 5 },
  cave: { firstMission: 6, lastMission: 10 },
  desert: { firstMission: 11, lastMission: 16 },
  fair: { firstMission: 17, lastMission: 23 },
  lavender: { firstMission: 24, lastMission: 28 },
  treetop: { firstMission: 29, lastMission: 29 },
  boat: { firstMission: 30, lastMission: 34 },
  pixelpast: { firstMission: 35, lastMission: 40 },
  kendrex: { firstMission: 41, lastMission: 46 },
});

/**
 * @param {string} surface
 * @param {number} globalMissionIndex
 * @returns {boolean}
 */
export function isGlobalMissionOnExploreUnknownMinigameSurface(surface, globalMissionIndex) {
  const range = EXPLORE_UNKNOWN_MINIGAME_SURFACE_MISSION_RANGES[surface];
  if (!range) {
    throw new Error(`isGlobalMissionOnExploreUnknownMinigameSurface: unknown surface ${JSON.stringify(surface)}`);
  }
  return (
    Number.isInteger(globalMissionIndex) &&
    globalMissionIndex >= range.firstMission &&
    globalMissionIndex <= range.lastMission
  );
}

/**
 * @param {string} surface
 * @returns {boolean}
 */
export function isExploreUnknownMinigameEnabledForSurface(surface) {
  const entry = EXPLORE_UNKNOWN_MINIGAME_SURFACES[surface];
  if (!entry) {
    throw new Error(`isExploreUnknownMinigameEnabledForSurface: unknown surface ${JSON.stringify(surface)}`);
  }
  return entry.enabled === true && typeof entry.minigameId === 'string' && entry.minigameId.length > 0;
}
